import { AUTH_TYPE } from "rainbow-web-sdk/services/core/auth.service";

export interface BackChannelSession {
    id: string,
    pollingUrl: string,
    token: string
}

/**
 * @class
 * @name AuthSettings
 * @description
 *      This class contains the authentication methods available for an identified user.<br>
 *      Depending of the user's Login Policy setttings, it can either login with its RAINBOW account
 *      or thru a Single Sign-On (SSO) solution (for exemple a SAML authentication system) set up
 *      in its enterprise.
 */
export class AuthSettings {

    public type: AUTH_TYPE | null = null;
    public loginUrl: string | null = null;
    public logoutUrl: string | null = null;
    public safetyUrl: string | null = null;
    public codeUrl: string | null = null;
    public challenge: string | null = null;
    public useBackChannel: boolean | null = null;
    public backChannelSession: BackChannelSession | null = null;

    public userId: string | null = null;

    public static createFromData(data: any): AuthSettings { return new AuthSettings(data); }

    constructor(authURLs: any[]) {
        if (authURLs.length > 0) {
            let authSettings: any = {};
            if (authURLs.length === 1) { authSettings = authURLs[0]; }
            else {
                // If both SAML and RAINBOW authentication methods are available for this user,
                // RAINBOW login url can be used as a safety url (when SSO login fails)
                authSettings = authURLs.reduce((settings, authUrl) => {
                    if (authUrl.type === AUTH_TYPE.RAINBOW && settings.type !== AUTH_TYPE.RAINBOW) {
                        settings.safetyUrl = authUrl.url || authUrl.loginUrl;
                        return settings;
                    }
                    authUrl.safetyUrl = settings.url || settings.loginUrl;
                    return authUrl;
                });
            }

            this.type = authSettings.type;
            this.loginUrl = authSettings.url || authSettings.loginUrl;
            this.logoutUrl = authSettings.logoutUrl;
            this.safetyUrl = authSettings.safetyUrl;
            this.codeUrl = authSettings.codeUrl;
            this.challenge = authSettings.challenge;
            this.useBackChannel = authSettings.useBackchannel;
            this.backChannelSession = authSettings.backchannelSession;
        }
    }

    /**
     * @public
     * @function setRainbowAppAuth
     * @memberof authSettings
     * @description Adds the fieldname as extra parameter of the login and/or logout url with the value generated by the function.
     * @param {string} name the field name to add as parameter
     * @param {Function} fn the generator function that generated the value.
     */
    public setParameter(name: string, fn: any) {
        switch (name) {
            case 'uid':
                this.userId = fn(); // the Rainbow user unique identifier: typicaly his login email
                break;
            case 'x-rainbow-app-auth':
                // Extra HTTP request header must be passed in URL.
                // If SAML authentication method is used for this user, extra login request fields must be passed in the url.
                if (this.type !== AUTH_TYPE.RAINBOW && this.loginUrl) {
                    const url = new URL(this.loginUrl);
                    const passkey = url.searchParams.get("challenge");
                    const appAuth = fn(passkey);
                    url.searchParams.append(name, appAuth); // single URL parameter value is encoded
                    this.loginUrl = url.href;
                }
                break;
            case 'token':
                if (this.type !== AUTH_TYPE.RAINBOW && this.logoutUrl) {
                    const url = new URL(this.logoutUrl);
                    const tkn = fn();
                    url.searchParams.append(name, tkn); // single URL parameter value is encoded
                    this.logoutUrl = url.href;
                }
                break;
            case 'safetyUrl':
                // force a safety login url, if not already set, when SSO login is enabled for the identified user
                if (this.type !== AUTH_TYPE.RAINBOW && !this.safetyUrl) { this.safetyUrl = fn(); }
                break;
            default:
                break;
        }
    }
}
